import { Maybe } from '@monetize/types/common';
import clamp from 'lodash/clamp';

/**
 * `invoiceDelay` is a signed day count applied to the scheduled billing date.
 * Negative shifts invoice generation earlier, positive delays it.
 *
 * The bounds mirror the backend: `@Min(-30) @Max(30)` on InvoiceAutomationDto and
 * `@Range(min = -30, max = 30)` on the BillGroup entity.
 */
export const INVOICE_DELAY_MAX_DAYS = 30;

export type InvoiceDelayDirection = 'AS_SCHEDULED' | 'EARLIER' | 'AFTER';

/**
 * The usage-product caveat previously lived on a hover icon attached to the single
 * `-30` dropdown option. Any earlier offset hits the same behavior, so it now sits
 * on the field itself.
 */
export const INVOICE_DELAY_TOOLTIP = `Shifts invoice generation relative to the scheduled billing date, up to ${INVOICE_DELAY_MAX_DAYS} days earlier or later. If Usage products are on a Quote, Bill Group Invoice Creation Timing is set to ‘As Scheduled’ to ensure all usage data is calculated before invoice generation.`;

export const getInvoiceDelayDirection = (
  invoiceDelay: Maybe<number>,
): InvoiceDelayDirection => {
  if (!invoiceDelay) {
    return 'AS_SCHEDULED';
  }
  return invoiceDelay < 0 ? 'EARLIER' : 'AFTER';
};

/**
 * Build the signed value the API expects from an unsigned day count plus a direction.
 * Days are clamped to 1-30 so the control can never emit an out-of-range value.
 */
export const toInvoiceDelayValue = (
  days: Maybe<number>,
  direction: InvoiceDelayDirection,
): number => {
  if (direction === 'AS_SCHEDULED') {
    return 0;
  }
  const clampedDays = clamp(
    Math.abs(Math.trunc(days || 0)),
    1,
    INVOICE_DELAY_MAX_DAYS,
  );
  return direction === 'EARLIER' ? -clampedDays : clampedDays;
};

/** Short label for read-only display, e.g. "7 days earlier than scheduled". */
export const formatInvoiceDelay = (invoiceDelay: Maybe<number>): string => {
  if (!invoiceDelay) {
    return 'Keep as scheduled';
  }
  const days = Math.abs(invoiceDelay);
  const dayLabel = days === 1 ? 'day' : 'days';
  return invoiceDelay < 0
    ? `${days} ${dayLabel} earlier than scheduled`
    : `${days} ${dayLabel} after scheduled`;
};

/** Full sentence shown beneath the input so the offset direction is unambiguous. */
export const describeInvoiceDelay = (invoiceDelay: Maybe<number>): string => {
  if (!invoiceDelay) {
    return 'Invoices are created on the scheduled billing date.';
  }
  const days = Math.abs(invoiceDelay);
  const dayLabel = days === 1 ? 'day' : 'days';
  return invoiceDelay < 0
    ? `Invoices are created ${days} ${dayLabel} before the scheduled billing date.`
    : `Invoices are created ${days} ${dayLabel} after the scheduled billing date.`;
};
